import React, { useEffect, memo, useContext, useRef } from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { PromoWrapper, getFormattedDesignData } from 'wsm-specials-display';
import { setClassNames } from 'ddc-classnames-js';
import { useSitemap, useLabels } from 'wsm-common-data';
import { promoComponentPropTypes } from '../../utilities/commonPropTypes';
import { getDestinationLink } from '../../utilities/links';
import { getCouponStatus } from '../../utilities/aspect';
import { PrefsContext } from '../../contexts';

const Promo = ({
	promoIndex,
	isMobile = false,
	isVisible = true,
	promo,
	role = null,
	positionLabel = null,
	roleDescription = null,
	noNeedAriaCurrent = false,
	isCurrent = false
}) => {
	const { aspectRatio, enableCarousel } = useContext(PrefsContext);

	const labels = useLabels();
	const sitemap = useSitemap();
	const promoWrapper = useRef(null);
	const { designs } = useSelector((state) => state.designs);
	const account = useSelector((state) => state.account);
	const personalized = useSelector((state) => state.personalized);
	const { id: accountId } = useSelector((state) => state.accountId);
	const isCoupon = getCouponStatus(aspectRatio);
	const isCouponMobile = isCoupon && isMobile;
	const {
		autoGenerated = false,
		condition = 'NEW',
		customAssets = {},
		destinationLabel = '',
		destinationUrl = '',
		id,
		inventoryCount = 0,
		oemId = null,
		title = '',
		type
	} = promo;
	const isCustomAsset = customAssets && customAssets[aspectRatio];
	const isDesktop4x3 = aspectRatio === 'Desktop4x3';
	const isDesktop9x16 = aspectRatio === 'Desktop9x16';
	const classes = [
		'promo',
		`promo-type-${type.toLowerCase()}`,
		isCustomAsset ? 'custom-asset' : 'non-custom-asset',
		isCouponMobile && enableCarousel === 'false' ? 'mb-4' : '',
		aspectRatio === 'Desktop9x16' || aspectRatio === 'Mobile9x16'
			? 'vertical'
			: '',
		(isDesktop4x3 || isDesktop9x16) && enableCarousel === 'true'
			? 'mx-xl-4'
			: ''
	];

	const { url: ctaUrl, isFullSlideLink } = getDestinationLink({
		condition,
		label: destinationLabel,
		url: destinationUrl,
		inventoryCount,
		isCustomAsset,
		labels,
		sitemap,
		type
	});

	useEffect(() => {
		let slideInScriptTimeout;
		// Refreshing our ui-dialog module
		if (typeof window !== 'undefined' && isVisible) {
			if (isMobile) {
				slideInScriptTimeout = setTimeout(
					() => window.DDC.mobile.slidein.init(),
					500
				);
			} else if (
				typeof window.jQuery === 'function' &&
				window?.DDC?.modules
			) {
				$(document).trigger('modulesRefresh', {
					modules: {
						dialog: window?.DDC?.modules?.dialog
					}
				});
			}
		}
		return () => {
			clearTimeout(slideInScriptTimeout);
		};
	}, [isMobile, isVisible]);

	if (!isVisible) {
		return null;
	}

	return (
		<div
			className={setClassNames(classes)}
			data-promo-id={id}
			data-oem-id={oemId}
			data-title={!oemId ? title : null}
			// data-promo-eo={false}
			data-auto-generated={autoGenerated}
			data-url-href={isFullSlideLink ? ctaUrl : null}
			role={role}
			/* eslint-disable no-nested-ternary */
			aria-current={
				noNeedAriaCurrent
					? null
					: isCurrent || isVisible
					? 'true'
					: 'false'
			}
			aria-roledescription={roleDescription}
			aria-label={positionLabel}
			ref={promoWrapper}
		>
			<PromoWrapper
				promo={promo}
				accountId={accountId}
				account={account}
				personalized={personalized}
				aspectRatioProp={aspectRatio}
				designConfig={getFormattedDesignData(promo, designs)}
				promoIndex={promoIndex}
			/>
		</div>
	);
};

Promo.propTypes = {
	isMobile: PropTypes.bool,
	noNeedAriaCurrent: PropTypes.bool,
	isVisible: PropTypes.bool,
	isCurrent: PropTypes.bool,
	positionLabel: PropTypes.string,
	promo: PropTypes.shape(promoComponentPropTypes),
	role: PropTypes.string,
	roleDescription: PropTypes.string,
	promoIndex: PropTypes.number
};

export default memo(Promo);
